import type {
  CreateAssetTypeRequestDto,
  CreateSubsystemRequestDto,
  CreateComponentClassRequestDto,
  CreateParameterNameRequestDto,
  ComponentClassResponseDto,
  ComponentTypeStatus,
} from '@evimo/client';
import type { ParsedCsvRow } from '~/app/components/knowledgeBase/componentSchemaManager/ComponentSchemaManager.types';
import { parseStringList } from '~/app/hooks/knowledgeBase/stringListUtils';
import {
  assetTypesApi,
  subsystemsApi,
  componentClassesApi,
  eventStatesApi,
  parameterNamesApi,
  componentTypeApi,
} from './api-instances';
import {
  AssetModelCatalogApi,
  AssetTypeManufacturerModelCatalogApi,
  ManufacturerCatalogApi,
} from './catalog.api';
import { tryParseJson } from './utils';
import { createCsvFileFromRows } from '~/app/utils/convertToCsv';
import type { ManufacturerStatus } from '@evimo/types';

export async function uploadCsv(
  configName: string,
  rows?: ParsedCsvRow[],
  file?: File,
): Promise<void> {
  if (configName === 'Asset Type' && rows) {
    const dtos: CreateAssetTypeRequestDto[] = rows.map((r) => ({
      code: r.code,
      name: r.name,
      description: r.description || undefined,
      metadataSchema: tryParseJson(r.metadataSchema),
    }));
    await assetTypesApi.assetTypeControllerBatchImport({
      batchImportAssetTypesRequestDto: { items: dtos },
    });
  } else if (configName === 'Subsystem' && rows) {
    const dtos: CreateSubsystemRequestDto[] = rows.map((r) => ({
      code: r.code,
      name: r.name,
      description: r.description || undefined,
      metadataSchema: tryParseJson(r.metadataSchema),
    }));
    await subsystemsApi.subsystemControllerBatchImport({
      batchImportSubsystemsRequestDto: { items: dtos },
    });
  } else if (configName === 'Component Class' && rows) {
    const existing = await componentClassesApi.componentClassControllerFindAll({
      limit: 1000,
      page: 1,
    });
    const codeToId = new Map<string, string>();
    (existing.data || []).forEach((c: ComponentClassResponseDto) => {
      if (c.code) codeToId.set(String(c.code), c.id);
    });

    const dtos: CreateComponentClassRequestDto[] = rows.map((r) => {
      let parentId: string | undefined = undefined;
      let parentCode: string | undefined = undefined;
      const parentVal = r.parentCode;
      if (parentVal && String(parentVal).trim() !== '') {
        const mapped = codeToId.get(String(parentVal));
        if (mapped) parentId = mapped;
        else parentCode = String(parentVal);
      }
      return {
        code: r.code,
        name: r.name,
        description: r.description?.trim() ? r.description : undefined,
        synonyms: parseStringList(r.synonyms),
        parentId,
        parentCode,
      };
    });

    await componentClassesApi.componentClassControllerBatchImport({
      batchImportComponentClassesRequestDto: { items: dtos },
    });
  } else if (configName === 'Event State') {
    let fileToUpload = file;
    if (rows && rows.length > 0) {
      fileToUpload = createCsvFileFromRows(rows, 'event_state_corrected.csv');
    }

    if (fileToUpload) {
      const formData = new FormData();
      formData.append('file', fileToUpload);
      await eventStatesApi.eventStateControllerBatchImport({
        file: formData.get('file') as File,
      });
    }
  } else if (configName === 'Parameter Name' && rows) {
    const dtos: CreateParameterNameRequestDto[] = rows.map((r) => ({
      code: r.code,
      name: r.name,
      unit: r.unit ?? '',
    }));
    await parameterNamesApi.parameterNameControllerBatchImport({
      batchImportParameterNamesRequestDto: { items: dtos },
    });
  } else if (configName === 'Component Type') {
    // No batch import for Component Type yet
  }
}

export async function deleteRow(configName: string, id: string): Promise<void> {
  const resolver: Record<string, () => Promise<void>> = {
    'Asset Type': () => assetTypesApi.assetTypeControllerDelete({ id }),
    Subsystem: () => subsystemsApi.subsystemControllerDelete({ id }),
    'Component Class': () =>
      componentClassesApi.componentClassControllerDelete({ id }),
    'Event State': () => eventStatesApi.eventStateControllerDelete({ id }),
    'Parameter Name': () =>
      parameterNamesApi.parameterNameControllerDelete({ id }),
    'Component Type': () =>
      componentTypeApi.componentTypeControllerDelete({ id }),
    Manufacturer: () => ManufacturerCatalogApi.delete(id),
    'Asset Model': () => AssetModelCatalogApi.delete(id),
    'Asset Type Manufacturer Model': () =>
      AssetTypeManufacturerModelCatalogApi.delete(id),
  };

  const action = resolver[configName];

  if (!action) {
    throw new Error(`Delete is not supported for "${configName}"`);
  }

  await action();
}

async function saveAssetType(row: ParsedCsvRow, id?: string): Promise<void> {
  const dto = {
    code: row.code,
    name: row.name,
    description: row.description || undefined,
    metadataSchema: tryParseJson(row.metadataSchema),
  };
  if (!id) {
    await assetTypesApi.assetTypeControllerCreate({
      createAssetTypeRequestDto: dto,
    });
  } else {
    await assetTypesApi.assetTypeControllerUpdate({
      id,
      updateAssetTypeRequestDto: dto,
    });
  }
}

async function saveSubsystem(row: ParsedCsvRow, id?: string): Promise<void> {
  const dto = {
    code: row.code,
    name: row.name,
    description: row.description || undefined,
    metadataSchema: tryParseJson(row.metadataSchema),
  };
  if (!id) {
    await subsystemsApi.subsystemControllerCreate({
      createSubsystemRequestDto: dto,
    });
  } else {
    await subsystemsApi.subsystemControllerUpdate({
      id,
      updateSubsystemRequestDto: dto,
    });
  }
}

async function saveComponentClass(
  row: ParsedCsvRow,
  id?: string,
): Promise<void> {
  // Resolve parentCode -> parentId when saving a single row
  let parentId: string | undefined = undefined;
  let parentCode: string | undefined = undefined;
  const parentVal = row.parentCode;
  if (parentVal && String(parentVal).trim() !== '') {
    const existing = await componentClassesApi.componentClassControllerFindAll({
      limit: 1000,
      page: 1,
    });
    const codeToId = new Map<string, string>();
    (existing.data || []).forEach((c: ComponentClassResponseDto) => {
      if (c.code) codeToId.set(String(c.code), c.id);
    });
    const mapped = codeToId.get(String(parentVal));
    if (mapped) parentId = mapped;
    else parentCode = String(parentVal);
  }

  const dto = {
    code: row.code,
    name: row.name,
    description: row.description?.trim() ? row.description : undefined,
    synonyms: parseStringList(row.synonyms),
    parentId,
    parentCode,
  };
  if (!id) {
    await componentClassesApi.componentClassControllerCreate({
      createComponentClassRequestDto: dto,
    });
  } else {
    await componentClassesApi.componentClassControllerUpdate({
      id,
      updateComponentClassRequestDto: dto,
    });
  }
}

async function saveEventState(row: ParsedCsvRow, id?: string): Promise<void> {
  const dto = {
    code: String(row.code),
    name: row.name,
  };
  if (!id) {
    await eventStatesApi.eventStateControllerCreate({
      createEventStateRequestDto: dto,
    });
  } else {
    await eventStatesApi.eventStateControllerUpdate({
      id,
      updateEventStateRequestDto: dto,
    });
  }
}

async function saveParameterName(
  row: ParsedCsvRow,
  id?: string,
): Promise<void> {
  const dto = {
    code: String(row.code),
    name: row.name,
    unit: row.unit ?? '',
  };
  if (!id) {
    await parameterNamesApi.parameterNameControllerCreate({
      createParameterNameRequestDto: dto,
    });
  } else {
    await parameterNamesApi.parameterNameControllerUpdate({
      id,
      updateParameterNameRequestDto: dto,
    });
  }
}

async function saveComponentType(
  row: ParsedCsvRow,
  id?: string,
): Promise<void> {
  const dto = {
    name: row.name,
    status: (row.status as ComponentTypeStatus) || undefined,
    synonyms: parseStringList(row.synonyms),
  };
  if (!id) {
    await componentTypeApi.componentTypeControllerCreate({
      createComponentTypeRequestDto: dto,
    });
  } else {
    await componentTypeApi.componentTypeControllerUpdate({
      id,
      updateComponentTypeRequestDto: dto,
    });
  }
}

/** Resolves a Manufacturer *organization* by name for ATMM associations. */
async function resolveManufacturerOrganizationIdByName(
  name: string,
): Promise<string | undefined> {
  const trimmed = name.trim();
  if (!trimmed) return undefined;
  const { OrganizationService } =
    await import('~/app/services/organization/organization.service');
  const orgs = await OrganizationService.findAllOrganizations();
  const match = orgs.find(
    (o) =>
      o.organizationType === 'Manufacturer' &&
      o.name.toLowerCase() === trimmed.toLowerCase(),
  );
  return match?.id;
}

async function resolveAssetTypeIdByCode(code: string): Promise<string> {
  const trimmed = code.trim();
  const res = await assetTypesApi.assetTypeControllerFindAll({
    limit: 1000,
    page: 1,
  });
  const match = (res.data || []).find((a) => a.code === trimmed);
  if (!match) {
    throw new Error(`Unknown asset type code: ${trimmed}`);
  }
  return match.id;
}

async function resolveAssetModelIdByName(name: string): Promise<string> {
  const trimmed = name.trim();
  const res = await AssetModelCatalogApi.list({ limit: 1000, page: 1 });
  const match = (res.data || []).find(
    (m) => m.name.toLowerCase() === trimmed.toLowerCase(),
  );
  if (!match) {
    throw new Error(`Unknown asset model: ${trimmed}`);
  }
  return match.id;
}

async function saveManufacturer(row: ParsedCsvRow, id?: string): Promise<void> {
  const logName = row.logName?.trim() || null;
  const dto = {
    name: row.name,
    status: (row.status as ManufacturerStatus) || undefined,
    logName,
  };

  if (!id) {
    await ManufacturerCatalogApi.create(dto);
  } else {
    await ManufacturerCatalogApi.update(id, dto);
  }
}

async function saveAssetModel(row: ParsedCsvRow, id?: string): Promise<void> {
  const assetTypeId = await resolveAssetTypeIdByCode(row.assetTypeCode);
  const dto = {
    name: row.name,
    description: row.description ?? '',
    assetTypeId,
  };
  if (!id) {
    await AssetModelCatalogApi.create(dto);
  } else {
    await AssetModelCatalogApi.update(id, dto);
  }
}

async function saveAssetTypeManufacturerModel(
  row: ParsedCsvRow,
  id?: string,
): Promise<void> {
  const [assetTypeId, manufacturerId, assetModelId] = await Promise.all([
    resolveAssetTypeIdByCode(row.assetTypeCode),
    resolveManufacturerOrganizationIdByName(row.manufacturerName),
    resolveAssetModelIdByName(row.assetModelName),
  ]);
  if (!manufacturerId) {
    throw new Error(
      `Unknown manufacturer organization: ${row.manufacturerName}`,
    );
  }
  const dto = { assetTypeId, manufacturerId, assetModelId };
  if (!id) {
    await AssetTypeManufacturerModelCatalogApi.create(dto);
  } else {
    await AssetTypeManufacturerModelCatalogApi.update(id, dto);
  }
}

type SaveHandler = (row: ParsedCsvRow, id?: string) => Promise<void>;

const saveHandlers: Record<string, SaveHandler> = {
  'Asset Type': saveAssetType,
  Subsystem: saveSubsystem,
  'Component Class': saveComponentClass,
  'Event State': saveEventState,
  'Parameter Name': saveParameterName,
  'Component Type': saveComponentType,
  Manufacturer: saveManufacturer,
  'Asset Model': saveAssetModel,
  'Asset Type Manufacturer Model': saveAssetTypeManufacturerModel,
};

export async function saveRow(
  configName: string,
  key: string,
  row: ParsedCsvRow,
): Promise<void> {
  const isNew = key.startsWith('row_');
  const id = isNew ? undefined : key;

  const handler = saveHandlers[configName];
  if (handler) {
    await handler(row, id);
  }
}
